import {useState} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {Alert, Box, Button, CircularProgress, Container, Divider, Paper, TextField, Typography} from '@mui/material';
import {getAuth, updateEmail, updatePassword, updateProfile as firebaseUpdateProfile} from 'firebase/auth';
import {getDownloadURL, ref, uploadBytes} from 'firebase/storage';
import {db, storage} from '../firebase-config';
import {doc, getDoc, updateDoc} from 'firebase/firestore';

export default function ProfileEdit() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const auth = getAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(currentUser?.photoURL || '');

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Profilbild aktualisieren
      let photoURL = currentUser.photoURL;
      if (photoFile) {
        const storageRef = ref(storage, `profile-images/${currentUser.uid}/${Date.now()}-${photoFile.name}`);
        const snapshot = await uploadBytes(storageRef, photoFile);
        photoURL = await getDownloadURL(snapshot.ref);
      }

      // Profilaktualisierungen sammeln
      const updates = [];

      // Displayname und Foto aktualisieren
      if (displayName !== currentUser.displayName || photoURL !== currentUser.photoURL) {
        updates.push(firebaseUpdateProfile(auth.currentUser, {
          displayName: displayName,
          photoURL: photoURL
        }));
      }

      // E-Mail aktualisieren
      if (email !== currentUser.email) {
        updates.push(updateEmail(auth.currentUser, email));
      }

      // Passwort aktualisieren
      if (password) {
        if (password !== confirmPassword) {
          throw new Error('Die Passwörter stimmen nicht überein');
        }
        if (password.length < 6) {
          throw new Error('Das Passwort muss mindestens 6 Zeichen lang sein');
        }
        updates.push(updatePassword(auth.currentUser, password));
      }

      // Alle Änderungen durchführen
      await Promise.all(updates);

      // Aktualisiere auch das Profilbild in der Datenbank
      if (photoURL !== currentUser.photoURL) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          photoURL: photoURL
        });
      }

      setSuccess('Profil wurde erfolgreich aktualisiert');
      // Verwende den profilePath für die Umleitung
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();
      setTimeout(() => navigate('/profil/' + userData.profilePath), 1500);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Profils:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          border: 1,
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ 
          fontWeight: 'bold',
          background: 'linear-gradient(45deg, #cb7e00, #ffa726)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Profil bearbeiten
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          {/* Profilbild */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="profile-photo"
              type="file"
              onChange={handlePhotoChange}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <label htmlFor="profile-photo">
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    margin: '0 auto',
                    cursor: 'pointer',
                    border: '3px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Profilvorschau"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Foto hochladen
                      </Typography>
                    </Box>
                  )}
                </Box>
              </label>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                oder
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/avatar/edit')}
              >
                3D Avatar erstellen
              </Button>
            </Box>
          </Box>

          {/* Benutzername */}
          <TextField
            fullWidth
            label="Benutzername"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            margin="normal"
            size="small"
            autoComplete="username"
          />

          <Divider sx={{ my: 3 }} />

          {/* E-Mail */}
          <TextField
            fullWidth
            label="E-Mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            size="small"
            autoComplete="email"
          />

          <Divider sx={{ my: 3 }} />

          {/* Passwort */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Passwort ändern (optional)
          </Typography>
          <TextField
            fullWidth
            label="Neues Passwort"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            size="small"
            autoComplete="new-password"
          />
          <TextField
            fullWidth
            label="Passwort bestätigen"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            size="small"
            autoComplete="new-password"
          />

          {/* Fehlermeldungen */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Erfolgsmeldungen */}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          {/* Buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ ml: 'auto' }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                'Änderungen speichern'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
} 